import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { AdminProductsService } from "../../../pages/admin-pages/products/services/admin-products.service";
import { ProductAttributeDefinitionResponse } from "../../../pages/admin-pages/products/models/product-models";
import { AdminProductsActions } from "../../actions/admin-products.actions";
import { FailState } from "../../models/fail-state";
import { ProductAttributesAdminState } from "../../models/mp-admin/product-attributes-state";
import { AppState } from "../../reducers";
import { AdminProductsSelectors } from "../../selectors/admin-products.selectors";

@Injectable()
export class ProductAttributesAdminEffects {
	productAttributes = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.productAttributes.get),
			withLatestFrom(this.store.select(AdminProductsSelectors.productAttributes)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state, action.productId)
					? of(AdminProductsActions.productAttributes.getSuccess({ state, productId: action.productId }))
					: of(AdminProductsActions.productAttributes.getApi({ productId: action.productId }))
			)
		)
	);

	productAttributesApi = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.productAttributes.getApi),
			switchMap((action) =>
				this.service.getProductAttributes(action.productId).pipe(
					map((attributes: ProductAttributeDefinitionResponse[]) => {
						// Build attributes by product map for efficient filtering
						const attributesByProduct: { [productId: string]: ProductAttributeDefinitionResponse[] } = {};
						attributes.forEach((attr) => {
							if (!attributesByProduct[attr.productId]) {
								attributesByProduct[attr.productId] = [];
							}
							attributesByProduct[attr.productId].push(attr);
						});

						return AdminProductsActions.productAttributes.getSuccess({
							state: { attributes, attributesByProduct, loading: false, hasLoaded: true },
							productId: action.productId
						});
					}),
					catchError((error) =>
						of(AdminProductsActions.productAttributes.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private service: AdminProductsService,
		private store: Store<AppState>
	) {}

	private canUseStoreValue(state: ProductAttributesAdminState, productId?: string): boolean {
		if (
			state === undefined ||
			_.has(state, "error") ||
			state.loading ||
			state.hasLoaded !== true ||
			!_.has(state, "attributes") ||
			state.attributes === undefined
		) {
			return false;
		}

		// If requesting all attributes, check if data has loaded
		if (!productId) {
			return true;
		}

		// If requesting for specific product, check if we have the index built
		return (
			_.has(state, "attributesByProduct") &&
			state.attributesByProduct !== undefined &&
			_.has(state.attributesByProduct, productId)
		);
	}
}
